import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * Safe Area Insets 타입
 */
export interface SafeAreaInsets {
  /** 하단 Safe Area Inset (픽셀 값) */
  bottom: number;
  /** 하단 Safe Area CSS 문자열 (인라인 스타일용) */
  bottomCSS: string;
}

/**
 * Safe Area Hook
 *
 * iOS Safari 등에서 노치(Notch)나 홈 인디케이터로 인한
 * Safe Area Insets을 처리하기 위한 Hook입니다.
 *
 * 화면 회전(orientation change) 시 자동으로 값이 업데이트됩니다.
 *
 * 최적화:
 * - 테스트 요소를 한 번만 생성하고 재사용하여 DOM 조작 최소화
 * - 컴포넌트 언마운트 시 테스트 요소 정리
 *
 * @returns {SafeAreaInsets} Safe Area Insets 값
 *
 * @example
 * const { bottom, bottomCSS } = useSafeArea();
 *
 * // CSS 문자열 사용 (권장)
 * <div style={{ paddingBottom: bottomCSS }}>...</div>
 *
 * // 픽셀 값 사용 (계산 필요 시)
 * <div style={{ paddingBottom: `${bottom + 16}px` }}>...</div>
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/env
 */
export const useSafeArea = (): SafeAreaInsets => {
  const [bottom, setBottom] = useState<number>(0);

  // 테스트 요소 레퍼런스 (한 번 생성 후 재사용)
  const testElementRef = useRef<HTMLDivElement | null>(null);

  /**
   * Safe Area Inset 값 업데이트
   * 테스트 요소를 재사용하여 리플로우 최소화
   */
  const updateSafeArea = useCallback(() => {
    // 테스트 요소가 없으면 생성
    if (!testElementRef.current) {
      testElementRef.current = document.createElement('div');
      testElementRef.current.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        padding-bottom: env(safe-area-inset-bottom, 0px);
        pointer-events: none;
        visibility: hidden;
      `;
      document.body.appendChild(testElementRef.current);
    }

    const computedStyle = getComputedStyle(testElementRef.current);
    const bottomValue = parseFloat(computedStyle.paddingBottom) || 0;
    setBottom(bottomValue);
  }, []);

  useEffect(() => {
    // 초기 값 설정
    updateSafeArea();

    // 화면 회전 및 리사이즈 감지
    window.addEventListener('resize', updateSafeArea);
    window.addEventListener('orientationchange', updateSafeArea);

    // 미디어 쿼리를 통한 viewport 변경 감지 (iOS Safari 대응)
    const mediaQuery = window.matchMedia('(orientation: portrait)');
    const handleOrientationChange = () => {
      // orientationchange 이벤트 후 약간의 지연 필요 (브라우저가 값 업데이트 시간 필요)
      setTimeout(updateSafeArea, 100);
    };
    mediaQuery.addEventListener('change', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', updateSafeArea);
      window.removeEventListener('orientationchange', updateSafeArea);
      mediaQuery.removeEventListener('change', handleOrientationChange);

      // 테스트 요소 정리
      if (testElementRef.current && testElementRef.current.parentNode) {
        testElementRef.current.parentNode.removeChild(testElementRef.current);
        testElementRef.current = null;
      }
    };
  }, [updateSafeArea]);

  return {
    bottom,
    bottomCSS: 'max(env(safe-area-inset-bottom),0px)',
  };
};
